import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contacts, isApiError } from '@repletoryreach/sdk-api';
import { Stack, Button, Group, Text, Code } from '@repletoryreach/sdk-ui';
import { DataTable, TableShell, EmptyState, Alert, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { TextInput, Textarea, Select, Switch } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { formatCount } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';

/** The six value types, as the lookup codes the service accepts. */
const TYPES: contacts.ContactFieldType[] = ['text', 'number', 'date', 'boolean', 'select', 'url'];

/** The key shape the service enforces (FIELD_KEY_INVALID). */
const KEY_SHAPE = /^[a-z][a-z0-9_]{0,63}$/;

/** Keys the base contact already owns; the service refuses them as FIELD_KEY_RESERVED. */
const RESERVED = new Set([
  'id', 'msisdn', 'phone', 'name', 'email', 'locale', 'tags', 'notes',
  'opt_in', 'opt_in_channels', 'created_at', 'updated_at'
]);

/** The query key every consumer of the definitions shares, so a change here reaches the contact form and the filter. */
export const FIELDS_KEY = ['contact-fields'] as const;

/**
 * "Order Number" as a key: lower-case, runs of anything that is not a letter
 * or digit become one underscore, trimmed, prefixed when it would not start
 * with a letter. Mirrors `ContactFields.NormaliseKey` in the service so what
 * the form shows is what the service would have derived.
 */
export function keyFromLabel(label: string): string {
  let k = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  if (!/^[a-z]/.test(k)) k = `f_${k}`;
  return k.slice(0, 64);
}

/** The translated name of a type code. */
export function typeLabel(t: (k: string) => string, type: string): string {
  return t(`contacts.fields.type.${type}`);
}

/**
 * Contact fields: the extra details an organisation keeps about each person.
 *
 * `contacts.custom_fields` had been written by flows since the builder
 * shipped and shown to nobody. This page is where a field is named, typed
 * and switched on or off; the contact form, the list filter, segments, the
 * import and the export all read the same definitions.
 */
export default function SettingsContactFieldsPage() {
  const t = useT();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<contacts.ContactFieldDefinition | 'new' | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: FIELDS_KEY,
    queryFn: () => contacts.listFields()
  });
  const rows = (data?.data ?? []) as Indexed<contacts.ContactFieldDefinition>[];

  const invalidate = () => qc.invalidateQueries({ queryKey: FIELDS_KEY });

  const del = useMutation({
    mutationFn: (d: contacts.ContactFieldDefinition) => contacts.deleteField(d.id),
    onSuccess: (_r, d) => {
      invalidate();
      // The values left every contact with the definition.
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['contact'] });
      notifySuccess(t('contacts.fields.toast.deleted', { label: d.label }), t('contacts.fields.title'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('contacts.fields.toast.deleteFailed'))
  });

  /** §5: the body names the field and says how many contacts lose a value. */
  function confirmDelete(d: contacts.ContactFieldDefinition) {
    openConfirmModal({
      title: t('contacts.fields.delete.title', { label: d.label }),
      children: (
        <Stack gap="xs">
          <Text role="bodySmall">
            {d.contacts_with_value > 0
              ? t('contacts.fields.delete.body', { count: formatCount(d.contacts_with_value) })
              : t('contacts.fields.delete.bodyNone')}
          </Text>
          <Text role="caption" c="var(--rr-color-text-muted)">{t('contacts.fields.delete.hint')}</Text>
        </Stack>
      ),
      labels: { confirm: t('contacts.fields.delete.confirm'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => del.mutate(d)
    });
  }

  return (
    <Stack gap="lg">
      <TableShell
        title={t('contacts.fields.title')}
        description={t('contacts.fields.description')}
        totalLabel={rows.length ? String(rows.length) : undefined}
        labels={{
          table: t('table.view.table'),
          cards: t('table.view.cards'),
          list: t('table.view.list'),
          search: t('table.search'),
          perPage: t('table.perPage'),
          pageSizeOption: (n) => t('table.perPageOption', { n }),
          empty: t('table.noRecords'),
          range: (from, to, total) => t('table.range', { from, to, total })
        }}
        actions={<Button size="sm" onClick={() => setEditing('new')}>{t('contacts.fields.new')}</Button>}
        loading={isLoading}
        error={error ? (
          <Alert color="error" role="alert">
            <Group justify="space-between" gap="sm">
              <span>{t('contacts.fields.loadError')}</span>
              <Button size="xs" variant="default" onClick={() => refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        ) : undefined}
        isEmpty={rows.length === 0}
        empty={(
          <EmptyState
            title={t('contacts.fields.empty')}
            description={t('contacts.fields.emptyHint')}
            action={<Button size="sm" onClick={() => setEditing('new')}>{t('contacts.fields.new')}</Button>}
          />
        )}
      >
        <DataTable<Indexed<contacts.ContactFieldDefinition>>
          records={rows}
          loading={isLoading}
          idAccessor="id"
          onRowClick={(r) => setEditing(r)}
          columns={[
            { accessor: 'label', title: t('contacts.fields.columns.label') },
            {
              accessor: 'key',
              title: t('contacts.fields.columns.key'),
              render: (r) => <Code style={{ color: 'var(--rr-color-text-muted)' }}>{r.key}</Code>
            },
            { accessor: 'type', title: t('contacts.fields.columns.type'), render: (r) => typeLabel(t, r.type) },
            {
              accessor: 'contacts_with_value',
              title: t('contacts.fields.columns.usage'),
              render: (r) => r.contacts_with_value === 1
                ? t('contacts.fields.usage.one')
                : t('contacts.fields.usage', { count: formatCount(r.contacts_with_value) })
            },
            {
              accessor: 'is_active',
              title: t('contacts.fields.columns.active'),
              render: (r) => (
                <StatusBadge status={r.is_active ? 'healthy' : 'inactive'} size="xs">
                  {r.is_active ? t('contacts.fields.active') : t('contacts.fields.inactive')}
                </StatusBadge>
              )
            },
            {
              accessor: 'actions',
              title: t('common.actions'),
              render: (r) => (
                <Group gap="xs" wrap="nowrap">
                  <Button size="xs" variant="subtle" onClick={(e) => { e.stopPropagation(); setEditing(r); }}>
                    {t('action.edit')}
                  </Button>
                  <Button
                    size="xs"
                    variant="subtle"
                    color="red"
                    loading={del.isPending && del.variables?.id === r.id}
                    onClick={(e) => { e.stopPropagation(); confirmDelete(r); }}
                  >
                    {t('action.delete')}
                  </Button>
                </Group>
              )
            }
          ]}
        />
      </TableShell>

      <FieldDrawer target={editing} onClose={() => setEditing(null)} onSaved={invalidate} />
    </Stack>
  );
}

function FieldDrawer({
  target,
  onClose,
  onSaved
}: {
  target: contacts.ContactFieldDefinition | 'new' | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const isNew = target === 'new';
  const existing = isNew || target === null ? null : target;
  const open = target !== null;

  const [label, setLabel] = useState('');
  const [key, setKey] = useState('');
  // The key follows the label until the person edits it by hand.
  const [keyTouched, setKeyTouched] = useState(false);
  const [type, setType] = useState<contacts.ContactFieldType>('text');
  const [options, setOptions] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [keyError, setKeyError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const formKey = existing?.id ?? (isNew ? 'new' : null);
  useEffect(() => {
    if (!open) return;
    setLabel(existing?.label ?? '');
    setKey(existing?.key ?? '');
    setKeyTouched(!!existing);
    setType(existing?.type ?? 'text');
    setOptions((existing?.options ?? []).join('\n'));
    setDescription(existing?.description ?? '');
    setActive(existing?.is_active ?? true);
    setKeyError(null);
  }, [open, formKey]); // eslint-disable-line react-hooks/exhaustive-deps

  function onLabel(next: string) {
    setLabel(next);
    if (!keyTouched) { setKey(keyFromLabel(next)); setKeyError(null); }
  }

  const optionList = useMemo(
    () => options.split('\n').map((o) => o.trim()).filter(Boolean),
    [options]
  );

  const keyInvalid = isNew && key !== '' && !KEY_SHAPE.test(key);
  const keyReserved = isNew && RESERVED.has(key);
  const optionsMissing = type === 'select' && optionList.length === 0;
  const canSave = label.trim() !== '' && key !== '' && !keyInvalid && !keyReserved && !optionsMissing;

  async function save() {
    if (!canSave) return;
    setBusy(true);
    setKeyError(null);
    try {
      if (existing) {
        await contacts.updateField(existing.id, {
          label: label.trim(),
          description: description.trim(),
          is_active: active,
          ...(existing.type === 'select' ? { options: optionList } : {})
        });
        notifySuccess(t('contacts.fields.toast.updated', { label: label.trim() }), t('contacts.fields.title'));
      } else {
        await contacts.createField({
          key,
          label: label.trim(),
          type,
          ...(type === 'select' ? { options: optionList } : {}),
          ...(description.trim() ? { description: description.trim() } : {})
        });
        notifySuccess(t('contacts.fields.toast.created', { label: label.trim() }), t('contacts.fields.title'));
      }
      onSaved();
      onClose();
    } catch (e) {
      // The two refusals about the key belong on the key field, where the
      // person can fix them; anything else is a toast.
      const code = isApiError(e) ? e.code : null;
      if (code === 'FIELD_KEY_TAKEN') setKeyError(t('contacts.fields.form.keyTaken'));
      else if (code === 'FIELD_KEY_RESERVED') setKeyError(t('contacts.fields.form.keyReserved'));
      else if (code === 'FIELD_KEY_INVALID') setKeyError(t('contacts.fields.form.keyInvalid'));
      else notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('contacts.fields.toast.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer opened={open} onClose={onClose} title={isNew ? t('contacts.fields.new') : t('contacts.fields.edit')} position="right" size="md">
      <Stack gap="md" style={{ padding: 4 }}>
        <TextInput
          label={t('contacts.fields.form.label')}
          description={t('contacts.fields.form.labelHint')}
          value={label}
          onChange={(e) => onLabel(e.currentTarget.value)}
          maxLength={120}
          required
          autoFocus
        />

        {/* Derived from the label while the field is new; read-only after,
            with the reason under it, because the stored values are filed
            under it. */}
        <TextInput
          label={t('contacts.fields.form.key')}
          description={isNew ? t('contacts.fields.form.keyHint') : t('contacts.fields.form.keyLocked')}
          value={key}
          onChange={(e) => { setKeyTouched(true); setKey(e.currentTarget.value); setKeyError(null); }}
          error={keyError ?? (keyInvalid ? t('contacts.fields.form.keyInvalid') : keyReserved ? t('contacts.fields.form.keyReserved') : undefined)}
          disabled={!isNew}
          required
          autoComplete="off"
          spellCheck={false}
          style={{ fontFamily: 'var(--rr-font-mono, monospace)' }}
        />

        <Select
          label={t('contacts.fields.form.type')}
          description={isNew ? t('contacts.fields.form.typeHint') : t('contacts.fields.form.typeLocked')}
          value={type}
          onChange={(e) => setType(e.currentTarget.value as contacts.ContactFieldType)}
          disabled={!isNew}
          data={TYPES.map((x) => ({ value: x, label: typeLabel(t, x) }))}
        />

        {type === 'select' && (
          <Textarea
            label={t('contacts.fields.form.options')}
            description={t('contacts.fields.form.optionsHint')}
            value={options}
            onChange={(e) => setOptions(e.currentTarget.value)}
            error={optionsMissing && options !== '' ? t('contacts.fields.form.optionsRequired') : undefined}
            minRows={4}
            required
          />
        )}

        <Textarea
          label={t('contacts.fields.form.description')}
          description={t('contacts.fields.form.descriptionHint')}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          maxLength={300}
          minRows={2}
        />

        {!isNew && (
          <Switch
            label={t('contacts.fields.form.active')}
            description={t('contacts.fields.form.activeHint')}
            checked={active}
            onChange={setActive}
          />
        )}

        <Group justify="flex-end" gap="xs" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
          <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
          <Button onClick={save} loading={busy} disabled={!canSave}>{t('action.save')}</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
